import React, { useEffect, useRef, useState } from 'react';
import CustomInput from 'components/CustomInput/CustomInput';
import { BodyM, Caption } from 'components/Typography/Typography';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import Close from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import styles from 'assets/jss/material-dashboard-pro-react/components/customSearchStyle';
import { translate } from 'components/Translation';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyles = makeStyles(styles);

export interface ISearchElement {
	id: string;
	value: string;
	content: string | React.ReactElement;
}

interface ISearchProps {
	id: string;
	label: string;
	placeholder?: string;
	elements: ISearchElement[];
	noResults?: string | React.ReactElement;
	elementSelected: (e: any) => void;
	selectedElement: {
		value: string;
		id: string;
	} | null;
	error?: boolean;
	helperText?: string | React.ReactElement;
	loading?: boolean;
	onChange?: (e: any) => void;
	areMoreOptionsAvailable?: boolean;
	inputValueProp?: string;
	setInputValueProp?: (e: string) => void;
}

const MAX_DROPDOWN_HEIGHT = 182;
const MENU_ITEM_HEIGHT = 40;

const CustomSearch = ({
	elements,
	id,
	label,
	elementSelected,
	noResults = translate('no.results'),
	helperText,
	error,
	selectedElement,
	placeholder = translate('select'),
	loading,
	onChange,
	areMoreOptionsAvailable,
	inputValueProp,
	setInputValueProp,
}: ISearchProps) => {
	const classes = useStyles();
	const [inputValue, setInputValue] = useState<string>(inputValueProp || '');
	const [filteredElements, setFilteredElements] = useState<ISearchElement[]>([]);
	const [isOpen, setIsOpen] = React.useState(false);
	const [dropdownHeight, setDropdownHeight] = useState(0);
	const [isFocused, setIsFocused] = React.useState(false);
	const [activeItem, setActiveItem] = React.useState(0);
	const dropdown = useRef<HTMLDivElement>(null);
	const wrapper = useRef<HTMLDivElement>(null);
	const selected = selectedElement?.value;

	const dropdownClasses = classNames({
		[classes.dropdown]: true,
		[classes.open]: isOpen,
	});

	const searchWrapperClasses = classNames({
		[classes.searchWrapper]: true,
		[classes.searchWrapperOpen]: isOpen,
	});

	const selectedValueClasses = classNames({
		[classes.selectedValueWrapper]: true,
		[classes.valueSelected]: selectedElement?.value,
		[classes.valueFocused]: isFocused,
		[classes.valueError]: error,
	});

	const arrowClasses = classNames({
		[classes.arrowRotated]: isOpen,
		[classes.arrowIcon]: true,
	});

	const handleKeyPress = (event: KeyboardEvent) => {
		switch (event.key) {
			case 'ArrowDown':
				arrowDown();
				break;
			case 'ArrowUp':
				arrowUp();
				break;
			case 'Enter':
				enter();
				break;
			default:
				return;
		}
	};

	const arrowDown = () => {
		if (activeItem < filteredElements.length - 1) {
			setActiveItem(activeItem + 1);
		}
	};

	const arrowUp = () => {
		if (activeItem > 0) {
			setActiveItem(activeItem - 1);
		}
	};

	const enter = () => {
		if (filteredElements.length > 0 && activeItem < filteredElements.length) {
			elementSelected(filteredElements[activeItem]);
			setInputValue(filteredElements[activeItem].value);
			setIsOpen(false);
			setActiveItem(0);
		}
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (wrapper?.current && !wrapper?.current?.contains(event.target as HTMLDivElement)) {
			setIsOpen(false);
			setInputValue(inputValueProp || '');
		}
	};

	const elementClicked = (element: ISearchElement) => {
		elementSelected(element);
		setActiveItem(0);
		setIsOpen(false);
		setInputValue('');
		setInputValueProp && setInputValueProp('');
	};

	useEffect(() => {
		const lowerCaseInputValue = inputValue.toLowerCase();
		setFilteredElements(
			elements.filter((element: ISearchElement) => {
				return element.value && element.value.toLowerCase().indexOf(lowerCaseInputValue) >= 0;
			})
		);
	}, [inputValue, elements]);

	useEffect(() => {
		// @ts-ignore
		const scrollTop = dropdown.current.scrollTop;
		if (activeItem * 40 < scrollTop) {
			// @ts-ignore
			dropdown.current.scrollTop = activeItem * MENU_ITEM_HEIGHT;
		} else if ((activeItem + 1) * MENU_ITEM_HEIGHT > scrollTop + MAX_DROPDOWN_HEIGHT) {
			// @ts-ignore
			dropdown.current.scrollTop = (activeItem + 1) * MENU_ITEM_HEIGHT - MAX_DROPDOWN_HEIGHT;
		}
	}, [activeItem]);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	});

	useEffect(() => {
		let calculatedDropdownHeight = 0;
		if (isOpen && loading) {
			calculatedDropdownHeight = 80;
		} else if (isOpen && filteredElements.length * 40 >= MAX_DROPDOWN_HEIGHT) {
			calculatedDropdownHeight = MAX_DROPDOWN_HEIGHT;
		} else if (isOpen) {
			calculatedDropdownHeight = Math.max(1, filteredElements.length) * MENU_ITEM_HEIGHT + 20;
		}
		setDropdownHeight(calculatedDropdownHeight);
	}, [filteredElements, isOpen, loading]);

	return (
		<div
			ref={wrapper}
			tabIndex={0}
			onFocus={() => setIsFocused(true)}
			onBlur={() => setIsFocused(false)}
			className={searchWrapperClasses}
		>
			<div onClick={() => setIsOpen((value) => !value)}>
				{label && <Caption color="secondary-light">{label}</Caption>}
				<div className={selectedValueClasses} id={`${id}-select`}>
					<BodyM color={selected || isFocused ? 'primary-light' : 'disabled-light'}>
						{selectedElement?.value || placeholder}
					</BodyM>
					{selected && isFocused ? (
						<Close className={classes.closeIcon} onClick={() => elementSelected(null)} />
					) : (
						<KeyboardArrowDown className={arrowClasses} />
					)}
				</div>
			</div>
			{!isOpen && helperText && <Caption color="disabled-light">{helperText}</Caption>}
			<div className={dropdownClasses}>
				<div className={classes.searchInputWrapper}>
					<CustomInput
						id={id}
						formControlProps={{
							fullWidth: true,
						}}
						labelText={translate('search')}
						inputLabelClasses={classes.inputLabel}
						inputProps={{
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
								if (!onChange) {
									setActiveItem(0);
									setInputValue(event.target.value);
								} else {
									onChange(event.target.value);
									setInputValue(event.target.value);
								}
							},
							onKeyDown: (event: KeyboardEvent) => handleKeyPress(event),
							value: inputValue,
						}}
					/>
					<SearchIcon className={classes.searchIcon} />
				</div>
				<div ref={dropdown} className={classes.itemsWrapper} style={{ height: `${dropdownHeight}px` }}>
					{loading ? (
						<div>
							<CustomCircularProgress text={translate('loading')} loading={loading} />
						</div>
					) : (
						<div>
							{filteredElements.map((element, index) => (
								<div
									key={element.id}
									onMouseUp={() => elementClicked(element)}
									onMouseEnter={() => setActiveItem(index)}
									className={classNames({
										[classes.menuItem]: true,
										[classes.activeMenuItem]: activeItem === index,
										[classes.selectedMenuItem]: element.id === selectedElement?.id,
									})}
								>
									{element.content}
								</div>
							))}
							<ConditionalRender
								show={!!areMoreOptionsAvailable}
								onTrue={() => (
									<span className={classes.menuItem}>{translate('custom.search.more.options')}</span>
								)}
							/>
						</div>
					)}
					{(!filteredElements || filteredElements.length <= 0) && !loading && (
						<div>
							{typeof noResults === 'string' ? (
								<div className={classes.noResults}>
									<Caption color="secondary-light" disableMargin>
										{noResults}
									</Caption>
								</div>
							) : (
								noResults
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default CustomSearch;
